import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { setCookies, getCookie } from 'cookies-next';
import dynamic from 'next/dynamic';

import { Spacing } from '@bscom/components/Spacing';
import { tablets } from '@bscom/styling/breakpoints';
import { Markdown } from '@bscom/components/Markdown';
import { STANDARD_ARTICLE_WIDTH } from '@bscom/constants/widths';
import { DefaultHubspotForm } from '@bscom/components/HubspotForm';
import { HeaderSubheader } from '@bscom/components/HeaderSubheader';
import { RichText } from '@bscom/components/RichText';

const Wrapper = styled(Spacing)`
  background-color: ${({ theme }) => theme.colors.greyLightest};
  margin: ${({ hasFading }) => (hasFading ? '0' : '60px')} 15px 60px;
  padding: 46px 20px;
  text-align: center;
  position: relative;
  width: auto;

  ${({ hasFading }) =>
    hasFading
      ? `
    &::before {
      position: absolute;
      width: 100%;
      top: -400px;
      left: 0;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 75%
      );
      height: 400px;
      content: '';
      z-index: 99;
    }
  `
      : ''}

  @media (${tablets}) {
    margin: 0 auto 80px auto;
    padding: 108px;
  }
`;

const Header = styled(HeaderSubheader)`
  margin: 0 auto 38px;
`;

const Text = styled(Markdown)`
  margin: 30px auto 0;
  text-align: center;
`;

const GATES_COOKIE = 'gates_passed';

const richTextModules = {
  resourceVideo: dynamic(
    () => import('@bscom/components/Video').then((mod) => mod.ResourceVideo),
    {
      ssr: false,
    }
  ),
  resourceImage: dynamic(() =>
    import('@bscom/content-blocks/ResourceImage').then(
      (mod) => mod.ResourceImageWithSpacing
    )
  ),
  pageHubspotForm: dynamic(() =>
    import('@bscom/content-blocks/PageHubspotForm')
  ),
  emailSignup: dynamic(() => import('@bscom/content-blocks/EmailSignup')),
};

export const GatedContent = (props) => {
  const { slug, form, hasFading, children, gatedContent } = props;
  const { id, headline, form: formComponent, disclaimer } = form || {};

  const [gatesPassed, setGatesPassed] = useState([]);

  useEffect(() => {
    const gatesPassed = getCookie(GATES_COOKIE);

    if (gatesPassed) {
      setGatesPassed(JSON.parse(gatesPassed));
    }
  }, []);

  const hasContent = children || gatedContent;
  const isGated = hasContent && gatesPassed.indexOf(slug) === -1;

  if (!isGated || !form) {
    if (gatedContent) {
      return (
        <RichText content={gatedContent} customModules={richTextModules} />
      );
    }
    return children || null;
  }

  const formCallback = async () => {
    try {
      const newGates = [...gatesPassed, slug];

      setCookies(GATES_COOKIE, JSON.stringify(newGates), {
        maxAge: 900000,
        httpOnly: false,
        sameSite: 'strict',
      });

      setGatesPassed(() => newGates);

      await new Promise((resolve) => setTimeout(resolve, 9000));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Wrapper
      hasFading={hasFading}
      contentWidth={STANDARD_ARTICLE_WIDTH}
      id={id}
    >
      {headline && <Header centered {...headline} />}
      <DefaultHubspotForm {...formComponent} submitCallback={formCallback} />

      {disclaimer && <Text>{disclaimer}</Text>}
    </Wrapper>
  );
};
